import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AdSetEntry } from '../api/getAdSets';
import type { FieldValue, UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { ChangeEvent, MouseEvent } from 'react';
import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import { TextField } from 'components/common';
import { CopyAll } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import { queryClient } from 'lib/react-query';
import { queryKeys } from '../api';
import { isUndefined } from 'lodash-es';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface FormData extends FieldValue<any> {
  selectedAdSets: AdSetEntry[];
  selectedAdSetsBudgets: Record<string, string>;
}

interface AdSetBudgetFieldProps {
  cellParams: GridRenderCellParams<AdSetEntry>;
}

interface UseAdSetBudgetForm extends Pick<UseFormReturn<FormData>, 'setValue' | 'getValues'> {
  selectedAdSets: AdSetEntry[];
  currentRow: AdSetEntry | undefined;
  isSelected: boolean;
  isCBO: boolean;
  isABO: boolean;
  CBO_AdSetIds: string[];
  ABO_AdSetIds: string[];
  fieldName: string;
}

const useAdSetBudgetForm = (currentId: string): UseAdSetBudgetForm => {
  const { watch, setValue, getValues } = useFormContext<FormData>();

  const selectedAdSets = watch('selectedAdSets');

  const currentRow = useMemo(
    () => selectedAdSets?.find((r: AdSetEntry) => r.id === currentId),
    [currentId, selectedAdSets],
  );

  const isSelected = !!currentRow;
  const isCBO = isSelected && currentRow.is_cbo;
  const isABO = isSelected && !currentRow.is_cbo;

  const CBO_AdSetIds = useMemo(() =>
      selectedAdSets?.filter(adSet => adSet.is_cbo).map(adSet => adSet.id) ?? [],
    [selectedAdSets],
  );

  const ABO_AdSetIds = useMemo(() =>
      selectedAdSets?.filter(adSet => adSet.is_cbo).map(adSet => adSet.id) ?? [],
    [selectedAdSets],
  );

  const fieldName = useMemo(
    () => `selectedAdSetsBudgets.${currentId}`,
    [currentId],
  );

  return {
    selectedAdSets,
    currentRow,
    isSelected,
    isCBO,
    isABO,
    CBO_AdSetIds,
    ABO_AdSetIds,
    fieldName,
    setValue,
    getValues,
  };
};

export const AdSetBudgetField: FC<AdSetBudgetFieldProps> = (
  {
    cellParams,
  }) => {
  const cachedAdSetsData = queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.adSets })
    .flatMap((query) => query.state.data as AdSetEntry[])
    .filter((c) => !!c);

  const currentId: string = cellParams.row.id;

  const {
    CBO_AdSetIds,
    currentRow,
    isSelected,
    isCBO,
    fieldName,
    setValue,
    getValues,
  } = useAdSetBudgetForm(currentId);

  useEffect(() => {
    if (!isSelected) return;

    const currentValue: string = getValues(fieldName);
    const budgetValue = currentRow?.budget?.toString() ?? '';

    if (isUndefined(currentValue) && budgetValue) {
      setValue(fieldName, budgetValue, {
        shouldValidate: true,
      });
    }
  }, [getValues, setValue, isSelected, fieldName, currentRow?.budget]);

  const handleApplyToAll = useCallback((): void => {
    const budgetToAll: string = getValues(fieldName) as string;
    const selectedAdSetsBudgetsValue: Record<string, string> = getValues(
      'selectedAdSetsBudgets',
    );

    if (!selectedAdSetsBudgetsValue || !budgetToAll) return;

    const values: Record<string, string> = Object.fromEntries(
      cachedAdSetsData.map(({ id }) => [id, budgetToAll]),
    );

    setValue('selectedAdSetsBudgets', values, { shouldValidate: true });
  }, [cachedAdSetsData, setValue, getValues, fieldName]);

  const handleBoxClick = useCallback((e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  }, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    const newBudgetValue = event.target.value;

    if (!newBudgetValue) return;

    const selectedAdSetsBudgetsValue: Record<string, string> = getValues(
      'selectedAdSetsBudgets',
    );

    if (isCBO) {
      queueMicrotask(() => {
        const updatedBudgets = { ...selectedAdSetsBudgetsValue };
        CBO_AdSetIds.forEach(id => {
          updatedBudgets[id] = newBudgetValue;
        });
        setValue('selectedAdSetsBudgets', updatedBudgets, { shouldValidate: true });
      });
      return;
    }

    setValue('selectedAdSetsBudgets', {
      ...selectedAdSetsBudgetsValue,
      [currentId]: newBudgetValue,
    }, { shouldValidate: true });

  }, [isCBO, CBO_AdSetIds, getValues, setValue, currentId]);

  const { ref, inView } = useInView({
    threshold: 0,
  });

  if (!isSelected) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        height: '100%',
        gap: 1,
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'start',
        alignItems: 'center',
      }}
      onClick={handleBoxClick}
    >
      {inView && <>
        <TextField
          errorAsTooltip
          handleChange={handleChange}
          name={fieldName}
          sx={{
            '& .MuiInputBase-root': {
              pr: 0.25,
              minWidth: 120,
            },
          }}
          InputProps={{
            type: 'number' as const,
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={(t) => ({ '&': { fontSize: t.typography.caption.fontSize } })}
                >
                  {isCBO ? 'CBO $' : 'ABO $'}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <Box className={'MuiInputAdornment-positionEnd'}>
          <Button
            data-testid={'applyToAllBudgetCountButton'}
            onClick={handleApplyToAll}
            size={'small'}
            sx={{ textTransform: 'initial', px: 1 }}
            startIcon={<CopyAll />}
          >
            Apply to all
          </Button>
        </Box>
      </>}
    </Box>
  );
};

const arePropsEqual = (prevProps: AdSetBudgetFieldProps, nextProps: AdSetBudgetFieldProps): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.budget === nextRow.budget &&
    prevRow.is_cbo === nextRow.is_cbo &&
    prevProps.cellParams.hasFocus === nextProps.cellParams.hasFocus
  );
};

export const MemoizedAdSetBudgetField = memo(
  AdSetBudgetField,
  (prevProps, nextProps) => arePropsEqual(prevProps, nextProps),
);